import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Home,
  Compass,
  MessageCircle,
  UserRound,
} from "lucide-react";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import { OrgSocialActorProvider } from "../scripts/OrgSocialActorContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { API_BASE_URL } from "@/lib/apiBase";
import { orgSocialBasePath } from "@/lib/socialPaths";
import { resolveAvatarUrl } from "@/lib/avatarApi";
import OrgSocialCommunitiesMenu from "./orgSocial/OrgSocialCommunitiesMenu";
import SocialBottomNav, {
  isSocialMobileThreadPath,
} from "./SocialBottomNav";

const NAV = [
  { id: "home", label: "Home", icon: Home, end: true, suffix: "" },
  { id: "explore", label: "Explore", icon: Compass, suffix: "/explore" },
  {
    id: "messages",
    label: "Messages",
    icon: MessageCircle,
    suffix: "/messages",
  },
];

/**
 * Immersive workplace Circa Social shell — act as the org outside workplace chrome.
 * Nested routes: /orgs/:orgId/social/*
 */
export default function CommunitySpacePage() {
  const { orgId } = useParams();
  const location = useLocation();
  const { accessToken } = useAuth();
  const { setSelectedOrgId } = useOrgFilter();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [presence, setPresence] = useState(null);
  const [orgProfile, setOrgProfile] = useState(null);
  const [canPublish, setCanPublish] = useState(false);
  const [canManageSettings, setCanManageSettings] = useState(false);

  const basePath = orgSocialBasePath(orgId);

  useEffect(() => {
    if (orgId) setSelectedOrgId(orgId);
  }, [orgId, setSelectedOrgId]);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const loadShell = useCallback(async () => {
    if (!accessToken || !orgId) return;
    setLoading(true);
    setError(null);
    try {
      const [pres, prof, meRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/org/${orgId}/social/presence`, { headers }),
        axios.get(`${API_BASE_URL}/org/${orgId}/social/profile`, { headers }),
        axios
          .get(`${API_BASE_URL}/org/${orgId}/me`, { headers })
          .catch(() => null),
      ]);
      setPresence(pres.data);
      setOrgProfile(prof.data);
      setCanPublish(Boolean(pres.data?.can_social_publish));
      const me = meRes?.data;
      const caps = new Set(
        Array.isArray(me?.capabilities) ? me.capabilities : []
      );
      setCanManageSettings(
        caps.has("org.manage_settings") || Boolean(me?.is_owner)
      );
      if (!pres.data?.enabled) {
        setError("Social presence is not enabled for this organization.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load Circa Social"
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, orgId, headers]);

  useEffect(() => {
    loadShell();
  }, [loadShell]);

  if (loading) {
    return (
      <div
        data-social-plane
        className="flex justify-center py-20 min-h-svh circa-orbit-bg-subtle"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const orgName =
    orgProfile?.display_name || presence?.org?.name || "Organization";
  const handle = orgProfile?.username;
  const avatarUrl = resolveAvatarUrl(
    orgProfile?.avatar_url || presence?.org?.avatar_url || null
  );
  const initials = String(orgName)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "O";

  const profileViewPath = handle
    ? `${basePath}/org/${encodeURIComponent(handle)}`
    : `${basePath}/profile`;
  const profileEditPath = `${basePath}/profile`;

  const isProfileNavActive = (() => {
    const path = location.pathname || "";
    if (path === profileEditPath || path.startsWith(`${profileEditPath}/`)) {
      return true;
    }
    if (handle) {
      const view = `${basePath}/org/${encodeURIComponent(handle)}`;
      return path === view || path.startsWith(`${view}/`);
    }
    return false;
  })();

  const communities = orgProfile?.communities || [];
  const communityPath = (id) =>
    `${basePath}/c/${encodeURIComponent(String(id))}`;

  return (
    <OrgSocialActorProvider
      orgId={orgId}
      basePath={basePath}
      canPublish={canPublish}
      canManageSettings={canManageSettings}
      orgProfile={orgProfile}
      presence={presence}
      reloadShell={loadShell}
    >
      <div
        data-social-plane
        className="min-h-svh flex flex-col circa-orbit-bg-subtle text-foreground"
      >
        <header className="sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur-sm shadow-sm">
          <div className="mx-auto max-w-3xl flex min-w-0 items-center justify-between gap-2 px-3 py-2 md:px-4 md:py-3">
            <div className="flex min-w-0 items-center gap-2 md:gap-3">
              <Avatar className="h-8 w-8 shrink-0 ring-2 ring-primary/35 md:h-11 md:w-11">
                {avatarUrl ? (
                  <AvatarImage src={avatarUrl} alt={orgName} />
                ) : null}
                <AvatarFallback className="text-xs font-semibold bg-primary/15 text-foreground md:text-sm">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                  Circa Social
                </p>
                <p
                  className="truncate text-sm font-semibold leading-tight md:text-base"
                  title={`Acting as ${orgName} on Circa Social`}
                >
                  Acting as {orgName}
                </p>
                {handle ? (
                  <Link
                    to={profileViewPath}
                    className="hidden truncate text-sm text-muted-foreground hover:text-foreground hover:underline md:block"
                    title={`@${handle}`}
                  >
                    @{handle}
                  </Link>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <div className="md:hidden">
                <OrgSocialCommunitiesMenu
                  orgId={orgId}
                  communities={communities}
                  communityPath={communityPath}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 gap-1.5 h-10 md:h-9"
                asChild
              >
                <Link to="/dashboard" aria-label="Back to workplace">
                  <ArrowLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Back to workplace</span>
                  <span className="sm:hidden">Workplace</span>
                </Link>
              </Button>
            </div>
          </div>
        </header>

        <nav className="hidden border-b border-border bg-background/60 px-4 md:block">
          <div className="mx-auto max-w-3xl flex min-w-0 flex-wrap gap-1 py-2 items-center">
            {NAV.map((item) => {
              const Icon = item.icon;
              const to = `${basePath}${item.suffix}`;
              return (
                <NavLink
                  key={item.id}
                  to={to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm whitespace-nowrap transition-colors",
                      isActive
                        ? "bg-primary/15 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/60"
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </NavLink>
              );
            })}

            <OrgSocialCommunitiesMenu
              orgId={orgId}
              communities={communities}
              communityPath={communityPath}
            />

            <NavLink
              to={profileViewPath}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm whitespace-nowrap transition-colors",
                isProfileNavActive
                  ? "bg-primary/15 text-foreground font-medium"
                  : "text-muted-foreground hover:bg-muted/60"
              )}
            >
              <UserRound className="h-4 w-4 shrink-0" />
              Profile
            </NavLink>
          </div>
        </nav>

        <div
          className={`flex min-h-0 min-w-0 flex-1 flex-col${
            isSocialMobileThreadPath(location.pathname)
              ? ""
              : " max-md:pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))]"
          }`}
        >
          {error ? (
            <div className="mx-auto max-w-2xl px-4 pt-6">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          ) : null}

          {!canPublish && !error ? (
            <div className="mx-auto max-w-2xl px-4 pt-6">
              <Alert>
                <AlertDescription>
                  You can browse Circa Social here, but posting and engaging as{" "}
                  {orgName} requires the Social publish permission.
                </AlertDescription>
              </Alert>
            </div>
          ) : null}

          <Outlet />
        </div>
        {!isSocialMobileThreadPath(location.pathname) ? (
          <SocialBottomNav />
        ) : null}
      </div>
    </OrgSocialActorProvider>
  );
}
